/**
 * StudyConsentTemplateEditor - editor visual de una plantilla CI de Estudio.
 *
 * Permite modelar la plantilla por bloques tipados, sin tocar coordenadas
 * ni anclas (eso lo gestiona el backend automaticamente al generar el PDF).
 *
 * Bloques soportados (section.kind):
 *  - study_info_block   : auto. Imprime Codigo + Titulo del estudio.
 *  - static_text        : parrafo libre con placeholders {volunteer_full_name}, {study_code}, etc.
 *  - clauses            : lista de clausulas (cada una sera un CheckBox VIDsigner).
 *  - image_clauses      : igual que clauses pero marcable como opcional al crear el CI.
 *  - withdrawal_block   : parrafo de retirada (texto editable).
 *  - dual_signature     : Voluntario (tableta) + Investigador Principal (portal Validated ID).
 *
 * Las secciones se pueden reordenar (up/down), anadir, duplicar y eliminar.
 * Cada clausula dentro de una seccion tiene su propio id (estable) y texto.
 *
 * Vista previa: boton que llama al endpoint /preview del backend y abre el
 * PDF generado en una pestana nueva — sirve para iterar visualmente sobre la
 * plantilla antes de guardarla.
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  ArrowLeft, FloppyDisk, DownloadSimple, Plus, ArrowUp, ArrowDown,
  CopySimple, Trash, PencilSimple,
} from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SECTION_KIND_META = {
  study_info_block: { es: 'Datos del estudio (auto)', en: 'Study info (auto)', color: 'bg-stone-100 text-stone-700', auto: true },
  static_text: { es: 'Parrafo de texto', en: 'Static paragraph', color: 'bg-blue-50 text-blue-700' },
  clauses: { es: 'Lista de clausulas', en: 'Clause list', color: 'bg-emerald-50 text-emerald-700' },
  image_clauses: { es: 'Clausulas de imagenes (opcional por estudio)', en: 'Image clauses (study-toggleable)', color: 'bg-amber-50 text-amber-700' },
  withdrawal_block: { es: 'Bloque de retirada', en: 'Withdrawal block', color: 'bg-rose-50 text-rose-700' },
  dual_signature: { es: 'Doble firma (Voluntario + IP)', en: 'Dual signature', color: 'bg-violet-50 text-violet-700' },
};

const CATEGORY_OPTIONS = [
  { value: 'producto_sanitario', label: 'Producto Sanitario' },
  { value: 'medicamento', label: 'Medicamento' },
  { value: 'cosmetico', label: 'Cosmetico' },
  { value: 'suplemento', label: 'Suplemento Alimenticio' },
];

const DEFAULT_TEMPLATE = {
  category: 'producto_sanitario',
  name: '',
  description: '',
  header: { title_line_1: '', title_line_2: '' },
  sections: [],
  active: true,
};

const slug = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
const uid = (prefix = 's') => `${prefix}_${Math.random().toString(36).slice(2, 9)}`;

const sectionDefaults = (kind) => {
  switch (kind) {
    case 'study_info_block':
      return { id: uid('study_info'), kind, optional: false, title: 'Identificacion del estudio' };
    case 'static_text':
      return { id: uid('text'), kind, optional: false, text: '' };
    case 'clauses':
      return { id: uid('clauses'), kind, optional: false, title: '', clauses: [{ id: uid('c'), text: '', required: true }] };
    case 'image_clauses':
      return { id: uid('images'), kind, optional: true, title: 'Imagenes', clauses: [{ id: uid('c'), text: '', required: true }] };
    case 'withdrawal_block':
      return { id: uid('withdrawal'), kind, optional: false, title: 'Derecho a retirada', text: '' };
    case 'dual_signature':
      return { id: uid('signature'), kind, optional: false, title: 'Firmas', investigator_via_portal: true };
    default:
      return { id: uid(), kind, optional: false };
  }
};

export function StudyConsentTemplateEditor({ lang, template, onClose, onSaved }) {
  const isNew = !template;
  const [form, setForm] = useState(() => {
    if (template) {
      return JSON.parse(JSON.stringify(template));
    }
    return { ...DEFAULT_TEMPLATE };
  });
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [editClause, setEditClause] = useState(null);  // {sectionIdx, clauseIdx, clause}
  const [editSection, setEditSection] = useState(null); // {sectionIdx}

  useEffect(() => {
    // Reset when prop changes
    if (template) setForm(JSON.parse(JSON.stringify(template)));
    else setForm({ ...DEFAULT_TEMPLATE });
  }, [template]);

  const updateForm = (patch) => setForm(prev => ({ ...prev, ...patch }));
  const updateHeader = (patch) => setForm(prev => ({ ...prev, header: { ...(prev.header || {}), ...patch } }));

  const moveSection = (idx, delta) => {
    const next = [...form.sections];
    const dest = idx + delta;
    if (dest < 0 || dest >= next.length) return;
    [next[idx], next[dest]] = [next[dest], next[idx]];
    updateForm({ sections: next });
  };

  const removeSection = (idx) => {
    const next = form.sections.filter((_, i) => i !== idx);
    updateForm({ sections: next });
  };

  const duplicateSection = (idx) => {
    const clone = JSON.parse(JSON.stringify(form.sections[idx]));
    clone.id = uid(clone.kind);
    if (clone.clauses) {
      clone.clauses = clone.clauses.map(c => ({ ...c, id: uid('c') }));
    }
    const next = [...form.sections];
    next.splice(idx + 1, 0, clone);
    updateForm({ sections: next });
  };

  const addSection = (kind) => {
    const newSection = sectionDefaults(kind);
    updateForm({ sections: [...form.sections, newSection] });
  };

  const setSectionField = (idx, patch) => {
    const next = [...form.sections];
    next[idx] = { ...next[idx], ...patch };
    updateForm({ sections: next });
  };

  const setClauseField = (sectionIdx, clauseIdx, patch) => {
    const next = [...form.sections];
    const clauses = [...(next[sectionIdx].clauses || [])];
    clauses[clauseIdx] = { ...clauses[clauseIdx], ...patch };
    next[sectionIdx] = { ...next[sectionIdx], clauses };
    updateForm({ sections: next });
  };

  const addClause = (sectionIdx) => {
    const next = [...form.sections];
    const clauses = [...(next[sectionIdx].clauses || []), { id: uid('c'), text: '', required: true }];
    next[sectionIdx] = { ...next[sectionIdx], clauses };
    updateForm({ sections: next });
  };

  const removeClause = (sectionIdx, clauseIdx) => {
    const next = [...form.sections];
    const clauses = (next[sectionIdx].clauses || []).filter((_, i) => i !== clauseIdx);
    next[sectionIdx] = { ...next[sectionIdx], clauses };
    updateForm({ sections: next });
  };

  const moveClause = (sectionIdx, clauseIdx, delta) => {
    const next = [...form.sections];
    const clauses = [...(next[sectionIdx].clauses || [])];
    const dest = clauseIdx + delta;
    if (dest < 0 || dest >= clauses.length) return;
    [clauses[clauseIdx], clauses[dest]] = [clauses[dest], clauses[clauseIdx]];
    next[sectionIdx] = { ...next[sectionIdx], clauses };
    updateForm({ sections: next });
  };

  const validate = () => {
    if (!form.name?.trim()) return lang === 'es' ? 'Indica un nombre para la plantilla' : 'Template name is required';
    if (!form.category) return lang === 'es' ? 'Indica la categoria' : 'Category required';
    return null;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      // ensure clause ids non-empty
      const sections = form.sections.map(s => {
        if (s.clauses) {
          s.clauses = s.clauses.map(c => ({ ...c, id: c.id || uid('c'), text: (c.text || '').trim() })).filter(c => c.text);
        }
        return s;
      });
      const payload = { ...form, sections };
      if (template?.template_id) {
        await axios.put(
          `${API_URL}/api/study-consent-templates/${template.template_id}`,
          payload,
          { withCredentials: true },
        );
        toast.success(lang === 'es' ? 'Plantilla actualizada' : 'Template updated');
      } else {
        await axios.post(
          `${API_URL}/api/study-consent-templates`,
          payload,
          { withCredentials: true },
        );
        toast.success(lang === 'es' ? 'Plantilla creada' : 'Template created');
      }
      onSaved && onSaved();
    } catch (e) {
      const msg = e?.response?.data?.detail || (lang === 'es' ? 'Error al guardar' : 'Save error');
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    // Preview without saving by sending the template inline through the
    // preview endpoint of the saved template, or save-then-preview if new.
    // Simpler: require the template to be saved first for a stable preview.
    if (!template?.template_id) {
      toast.info(lang === 'es' ? 'Guarda la plantilla antes de previsualizar' : 'Save before previewing');
      return;
    }
    setPreviewing(true);
    try {
      const resp = await axios.post(
        `${API_URL}/api/study-consent-templates/${template.template_id}/preview`,
        {},
        { withCredentials: true, responseType: 'blob' },
      );
      const url = URL.createObjectURL(resp.data);
      window.open(url, '_blank', 'noopener');
    } catch {
      toast.error(lang === 'es' ? 'Error al generar vista previa' : 'Preview error');
    } finally {
      setPreviewing(false);
    }
  };

  // Section card renderer
  const renderSection = (section, idx) => {
    const meta = SECTION_KIND_META[section.kind] || { es: section.kind, en: section.kind, color: 'bg-stone-100 text-stone-700' };
    return (
      <Card key={section.id} className="border-stone-200" data-testid={`section-card-${idx}`}>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline" className={`text-xs ${meta.color}`}>{meta[lang] || meta.es}</Badge>
              {section.optional && (
                <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                  {lang === 'es' ? 'Opcional' : 'Optional'}
                </Badge>
              )}
              <span className="text-xs text-stone-500">#{idx + 1}</span>
            </div>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => moveSection(idx, -1)} disabled={idx === 0} title={lang === 'es' ? 'Subir' : 'Move up'}>
                <ArrowUp size={14} />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => moveSection(idx, +1)} disabled={idx === form.sections.length - 1} title={lang === 'es' ? 'Bajar' : 'Move down'}>
                <ArrowDown size={14} />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => duplicateSection(idx)} title={lang === 'es' ? 'Duplicar' : 'Duplicate'}>
                <CopySimple size={14} />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => removeSection(idx)} title={lang === 'es' ? 'Eliminar' : 'Delete'}>
                <Trash size={14} />
              </Button>
            </div>
          </div>

          {/* Body editor per section kind */}
          {section.kind === 'study_info_block' && (
            <p className="text-xs text-stone-500 italic">
              {lang === 'es'
                ? 'Imprime automaticamente Codigo y Titulo del estudio al generar el PDF.'
                : 'Auto-prints study Code and Title at PDF generation time.'}
            </p>
          )}

          {section.kind === 'dual_signature' && (
            <div className="space-y-2">
              <Input
                value={section.title || ''}
                onChange={(e) => setSectionField(idx, { title: e.target.value })}
                placeholder={lang === 'es' ? 'Titulo (opcional)' : 'Title (optional)'}
              />
              <div className="flex items-center gap-2">
                <Switch
                  checked={section.investigator_via_portal !== false}
                  onCheckedChange={(v) => setSectionField(idx, { investigator_via_portal: v })}
                />
                <span className="text-xs text-stone-600">
                  {lang === 'es'
                    ? 'El Investigador Principal firma a traves del portal Validated ID (no tableta)'
                    : 'PI signs through Validated ID portal (not tablet)'}
                </span>
              </div>
            </div>
          )}

          {(section.kind === 'static_text' || section.kind === 'withdrawal_block') && (
            <div className="space-y-2">
              {section.kind === 'withdrawal_block' && (
                <Input
                  value={section.title || ''}
                  onChange={(e) => setSectionField(idx, { title: e.target.value })}
                  placeholder={lang === 'es' ? 'Titulo' : 'Title'}
                />
              )}
              <Textarea
                value={section.text || ''}
                onChange={(e) => setSectionField(idx, { text: e.target.value })}
                placeholder={lang === 'es'
                  ? 'Texto del parrafo. Placeholders: {volunteer_full_name}, {volunteer_dni}, {study_code}, {study_title}, {investigator_name}, {today}'
                  : 'Paragraph text. Placeholders available.'}
                rows={4}
                className="font-mono text-sm"
              />
            </div>
          )}

          {(section.kind === 'clauses' || section.kind === 'image_clauses') && (
            <div className="space-y-3">
              <Input
                value={section.title || ''}
                onChange={(e) => setSectionField(idx, { title: e.target.value })}
                placeholder={lang === 'es' ? 'Titulo (opcional)' : 'Title (optional)'}
              />
              <div className="space-y-2">
                {(section.clauses || []).map((c, ci) => (
                  <div key={c.id} className="flex items-start gap-2 p-2 border border-stone-200 rounded-sm bg-stone-50">
                    <span className="text-xs text-stone-400 mt-2 w-6 text-right">{ci + 1}.</span>
                    <Textarea
                      value={c.text}
                      onChange={(e) => setClauseField(idx, ci, { text: e.target.value })}
                      placeholder={lang === 'es' ? 'Texto de la clausula' : 'Clause text'}
                      rows={2}
                      className="text-sm flex-1"
                    />
                    <div className="flex flex-col gap-1">
                      <Select
                        value={c.answer || 'check'}
                        onValueChange={(v) => setClauseField(idx, ci, { answer: v === 'check' ? undefined : v })}
                      >
                        <SelectTrigger className="h-8 w-32 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="check">{lang === 'es' ? 'Aceptar' : 'Accept'}</SelectItem>
                          <SelectItem value="yes_no">{lang === 'es' ? 'Si / No' : 'Yes / No'}</SelectItem>
                        </SelectContent>
                      </Select>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => moveClause(idx, ci, -1)} disabled={ci === 0}>
                          <ArrowUp size={12} />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => moveClause(idx, ci, +1)} disabled={ci === (section.clauses?.length || 0) - 1}>
                          <ArrowDown size={12} />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-red-600" onClick={() => removeClause(idx, ci)}>
                          <Trash size={12} />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => addClause(idx)}
                  className="gap-2"
                >
                  <Plus size={14} />
                  {lang === 'es' ? 'Anadir clausula' : 'Add clause'}
                </Button>
              </div>
            </div>
          )}

          <div className="flex items-center gap-2 pt-2 border-t border-stone-100">
            <Switch
              checked={!!section.optional}
              onCheckedChange={(v) => setSectionField(idx, { optional: v })}
            />
            <span className="text-xs text-stone-600">
              {lang === 'es'
                ? 'Seccion opcional (se puede activar o no por estudio)'
                : 'Optional section (toggleable per study)'}
            </span>
          </div>
        </CardContent>
      </Card>
    );
  };

  const addSectionButtons = useMemo(() => (
    <div className="flex items-center gap-2 flex-wrap">
      {Object.entries(SECTION_KIND_META).map(([kind, meta]) => (
        <Button
          key={kind}
          variant="outline"
          size="sm"
          onClick={() => addSection(kind)}
          className="gap-1.5 text-xs"
          data-testid={`add-section-${kind}`}
        >
          <Plus size={12} />
          {meta[lang] || meta.es}
        </Button>
      ))}
    </div>
  ), [lang]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-5" data-testid="study-consent-template-editor">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onClose} className="gap-1.5">
            <ArrowLeft size={16} />
            {lang === 'es' ? 'Volver' : 'Back'}
          </Button>
          <h2 className="text-xl font-semibold text-stone-900">
            {isNew
              ? (lang === 'es' ? 'Nueva plantilla CI de Estudio' : 'New Study CI template')
              : (lang === 'es' ? 'Editar plantilla' : 'Edit template')}
          </h2>
        </div>
        <div className="flex items-center gap-2">
          {!isNew && (
            <Button variant="outline" onClick={handlePreview} disabled={previewing} className="gap-2" data-testid="study-ci-editor-preview-btn">
              <DownloadSimple size={16} />
              {previewing ? '...' : (lang === 'es' ? 'Vista previa' : 'Preview')}
            </Button>
          )}
          <Button onClick={handleSave} disabled={saving} className="gap-2" data-testid="study-ci-editor-save-btn">
            <FloppyDisk size={16} />
            {saving
              ? (lang === 'es' ? 'Guardando...' : 'Saving...')
              : (lang === 'es' ? 'Guardar' : 'Save')}
          </Button>
        </div>
      </div>

      {/* Metadata */}
      <Card>
        <CardContent className="p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2 space-y-1">
            <Label className="text-xs">{lang === 'es' ? 'Nombre' : 'Name'}</Label>
            <Input value={form.name} onChange={e => updateForm({ name: e.target.value })} placeholder={lang === 'es' ? 'Ej. CI Producto Sanitario - BtH Body' : 'e.g. PS CI - BtH Body'} data-testid="study-ci-name" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{lang === 'es' ? 'Categoria' : 'Category'}</Label>
            <Select value={form.category} onValueChange={v => updateForm({ category: v })}>
              <SelectTrigger data-testid="study-ci-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORY_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-3 space-y-1">
            <Label className="text-xs">{lang === 'es' ? 'Descripcion' : 'Description'}</Label>
            <Input value={form.description || ''} onChange={e => updateForm({ description: e.target.value })} placeholder={lang === 'es' ? 'Descripcion breve' : 'Short description'} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{lang === 'es' ? 'Cabecera linea 1' : 'Header line 1'}</Label>
            <Input value={form.header?.title_line_1 || ''} onChange={e => updateHeader({ title_line_1: e.target.value })} placeholder="INVESTIGACION CLINICA CON..." />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{lang === 'es' ? 'Cabecera linea 2' : 'Header line 2'}</Label>
            <Input value={form.header?.title_line_2 || ''} onChange={e => updateHeader({ title_line_2: e.target.value })} placeholder="ANEXO IX" />
          </div>
          <div className="flex items-center gap-2 mt-6">
            <Switch checked={form.active !== false} onCheckedChange={v => updateForm({ active: v })} />
            <span className="text-xs text-stone-600">{lang === 'es' ? 'Plantilla activa (visible al importar en estudios)' : 'Active (visible when importing to studies)'}</span>
          </div>
        </CardContent>
      </Card>

      {/* Sections */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-stone-700">{lang === 'es' ? 'Secciones' : 'Sections'} ({form.sections.length})</h3>
        {form.sections.length === 0 && (
          <p className="text-sm text-stone-500 italic">
            {lang === 'es' ? 'Anade secciones para construir la plantilla.' : 'Add sections to build the template.'}
          </p>
        )}
        <div className="space-y-3">
          {form.sections.map((s, i) => renderSection(s, i))}
        </div>
        <Card className="border-dashed border-stone-300 bg-stone-50/50">
          <CardContent className="p-3 space-y-2">
            <p className="text-xs font-medium text-stone-600">
              {lang === 'es' ? 'Anadir seccion' : 'Add section'}
            </p>
            {addSectionButtons}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
